/**
 * sessionCoordinator.ts
 *
 * Single-responsibility module for cross-tab session coordination.
 *
 * Responsibilities:
 *  - Owns all sessionStorage flag keys (no raw strings leak into callers).
 *  - Owns the BroadcastChannel instance used for listening.
 *  - Exposes named functions so authService and App never touch storage keys directly.
 *
 * Design principles applied:
 *  - SRP  — only this module knows about cross-tab signalling mechanics.
 *  - OCP  — new event types can be added without touching callers.
 *  - DIP  — callers depend on this abstraction, not on BroadcastChannel/sessionStorage details.
 */

// ─── Constants ───────────────────────────────────────────────────────────────

const CHANNEL_NAME = 'wealthlane_session';

/** Marks the tab that is mid-way through a new-session OIDC redirect. */
const KEY_IS_NEW_SESSION_TAB = 'isNewSessionTab';

/**
 * Set immediately before broadcasting ANY event so the listener on the same tab
 * can identify and discard its own message (self-notification guard).
 * Stores the event type string so a single key covers all event types.
 */
const KEY_DID_INITIATE_EVENT = 'didInitiateSessionEvent';

// ─── Event Types ─────────────────────────────────────────────────────────────

export type SessionEventType = 'new_session_event' | 'logout_event';

export interface SessionEvent {
  type: SessionEventType;
}

// ─── Persistent listener channel ─────────────────────────────────────────────

/**
 * Module-level channel used exclusively for *receiving* messages.
 * Kept separate from the throw-away sender so the self-exclusion
 * guarantee of BroadcastChannel is not inadvertently relied upon
 * across page navigations.
 */
export const sessionChannel = new BroadcastChannel(CHANNEL_NAME);

// ─── Flag helpers (authService side) ─────────────────────────────────────────

/**
 * Called by authService.signInRedirectCallback() after a passwordless/
 * impersonation OIDC flow completes. Marks this tab as the session initiator
 * so App.tsx can broadcast the new_session_event at the right moment.
 */
export function markAsNewSessionTab(): void {
  sessionStorage.setItem(KEY_IS_NEW_SESSION_TAB, 'true');
}

// ─── Broadcast (App.tsx side) ─────────────────────────────────────────────────

/**
 * Returns true when this tab should broadcast a new_session_event.
 * Only valid outside the /callback route — broadcasting from /callback
 * causes self-notification because navigation creates a fresh listener
 * before the queued message is delivered.
 */
export function shouldBroadcastNewSession(): boolean {
  return (
    sessionStorage.getItem(KEY_IS_NEW_SESSION_TAB) === 'true' &&
    !window.location.href.includes('callback')
  );
}

/**
 * Sends a session event to all other tabs via a throw-away BroadcastChannel.
 *
 * A throw-away sender is used because the persistent sessionChannel listener
 * is a different object — BroadcastChannel self-exclusion only applies to the
 * exact instance that posted. The didInitiateSessionEvent guard ensures the
 * same-tab listener silently discards the message it would otherwise receive.
 */
function broadcast(type: SessionEventType): void {
  sessionStorage.setItem(KEY_DID_INITIATE_EVENT, type);

  const sender = new BroadcastChannel(CHANNEL_NAME);
  sender.postMessage({ type } satisfies SessionEvent);
  sender.close();
}

/**
 * Broadcasts logout and closes the listener so this tab never receives its own message.
 * Unlike new_session_event, the sessionStorage guard doesn't work for logout because
 * authService.logout() calls sessionStorage.clear() immediately after — wiping the
 * guard before the async listener fires. Closing the listener is the correct approach
 * since the tab is navigating away and no longer needs to receive events.
 */
export function broadcastLogout(): void {
  sessionChannel.close();

  const sender = new BroadcastChannel(CHANNEL_NAME);
  sender.postMessage({ type: 'logout_event' } satisfies SessionEvent);
  sender.close();
}

export function broadcastNewSession(): void {
  sessionStorage.removeItem(KEY_IS_NEW_SESSION_TAB);
  broadcast('new_session_event');
}

// ─── Listener (App.tsx side) ──────────────────────────────────────────────────

/**
 * Handles an incoming BroadcastChannel message.
 * Returns the event type if the caller should act on it, or null if the
 * message should be ignored (e.g. self-notification on the initiating tab).
 */
export function handleSessionMessage(event: MessageEvent): SessionEventType | null {
  const type = (event.data as SessionEvent | undefined)?.type;

  if (type !== 'logout_event' && type !== 'new_session_event') {
    return null;
  }

  const guard = sessionStorage.getItem(KEY_DID_INITIATE_EVENT);

  // Self-notification guard: if this tab initiated the event, discard silently.
  if (guard === type) {
    sessionStorage.removeItem(KEY_DID_INITIATE_EVENT);
    return null;
  }

  return type;
}
